import { type PermissionKey } from '@aysar/contracts';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/shared/components/feedback/EmptyState/EmptyState';
import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';

export interface PermissionRouteProps {
  /** The operator needs ANY of these. */
  anyOf: readonly PermissionKey[];
}

/**
 * Route-level authorization (N22).
 *
 * It renders a **forbidden** state rather than redirecting. A redirect from a
 * bookmarked deep link looks like the page moved, and the operator retries it
 * all afternoon; saying "you do not have access to this, ask an administrator"
 * is the answer that ends the loop.
 *
 * This is a RENDERING decision, never the enforcement. Every endpoint behind it
 * carries `@RequirePermissions` and evaluates the same keys through the same
 * `hasPermission` — so a client that skipped this gate gains nothing.
 */
export function PermissionRoute({ anyOf }: PermissionRouteProps) {
  const { canAny } = useHasPermission();
  const { t } = useTranslation('uiKit');

  if (!canAny(anyOf)) {
    return (
      <EmptyState title={t('state.forbiddenTitle')} description={t('state.forbiddenMessage')} />
    );
  }

  return <Outlet />;
}
